// src/pages/admin/ServiceAreas.js
import React, { useState } from "react";
import {
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  TextField,
  InputAdornment,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel, // Added missing import
  useTheme,
  alpha,
  Avatar
} from "@mui/material";
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  LocationOn as LocationIcon,
  Map as MapIcon,
  People as PeopleIcon,
  AttachMoney as MoneyIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
  MoreVert as MoreIcon,
} from "@mui/icons-material";

// Mock data for service areas
const mockServiceAreas = [
  {
    id: 1,
    name: "Manhattan",
    city: "New York",
    state: "NY",
    zipCodes: ["10001", "10002", "10003", "10004"],
    status: true,
    cleaners: 12,
    customers: 245,
    avgPrice: 125,
    coverage: "Full"
  },
  {
    id: 2,
    name: "Brooklyn",
    city: "New York",
    state: "NY",
    zipCodes: ["11201", "11205", "11215", "11217"],
    status: true,
    cleaners: 8,
    customers: 189,
    avgPrice: 115,
    coverage: "Full"
  },
  {
    id: 3,
    name: "Downtown LA",
    city: "Los Angeles",
    state: "CA",
    zipCodes: ["90012", "90013", "90014", "90015"],
    status: true,
    cleaners: 6,
    customers: 156,
    avgPrice: 135,
    coverage: "Partial"
  },
  {
    id: 4,
    name: "Chicago Loop",
    city: "Chicago",
    state: "IL",
    zipCodes: ["60601", "60602", "60603", "60604"],
    status: false,
    cleaners: 0,
    customers: 0,
    avgPrice: 0,
    coverage: "Planning"
  },
  {
    id: 5,
    name: "Miami Beach",
    city: "Miami",
    state: "FL",
    zipCodes: ["33139", "33140", "33141"],
    status: true,
    cleaners: 4,
    customers: 98,
    avgPrice: 145,
    coverage: "Full"
  }
];

export default function ServiceAreas() {
  const theme = useTheme();
  const [searchTerm, setSearchTerm] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [editingArea, setEditingArea] = useState(null);
  const [newArea, setNewArea] = useState({
    name: "",
    city: "",
    state: "",
    zipCodes: [],
    status: true,
    coverage: "Full"
  });

  const filteredAreas = mockServiceAreas.filter(area =>
    area.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    area.city.toLowerCase().includes(searchTerm.toLowerCase()) ||
    area.state.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleOpenDialog = (area = null) => {
    if (area) {
      setEditingArea(area);
      setNewArea({ ...area });
    } else {
      setEditingArea(null);
      setNewArea({
        name: "",
        city: "",
        state: "",
        zipCodes: [],
        status: true,
        coverage: "Full"
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingArea(null);
  };

  const handleSaveArea = () => {
    // Here you would save to your backend
    console.log('Saving area:', newArea);
    handleCloseDialog();
  };

  const handleZipCodesChange = (e) => {
    const zipCodes = e.target.value.split(',').map(zip => zip.trim()).filter(zip => zip);
    setNewArea(prev => ({ ...prev, zipCodes }));
  };

  const getCoverageColor = (coverage) => {
    switch (coverage) {
      case 'Full': return 'success';
      case 'Partial': return 'warning';
      case 'Planning': return 'info';
      default: return 'default';
    }
  };

  const stats = [
    { label: 'Active Areas', value: '4', change: '+1' },
    { label: 'Total Cities', value: '5', change: '+2' },
    { label: 'Active Cleaners', value: '30', change: '+5' },
    { label: 'Covered Customers', value: '688', change: '+12%' },
  ];

  const states = [
    "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA",
    "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD",
    "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
    "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC",
    "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY"
  ];

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h4" fontWeight="bold">
            Service Areas
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Add Area
          </Button>
        </Box>
        <Typography variant="body1" color="text.secondary">
          Manage geographic areas where cleaning services are offered
        </Typography>
      </Box>

      {/* Stats */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {stats.map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card>
              <CardContent>
                <Typography variant="h4" fontWeight="bold" color="primary" gutterBottom>
                  {stat.value}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {stat.label}
                </Typography>
                <Chip 
                  label={stat.change} 
                  size="small" 
                  color="success"
                  variant="outlined"
                />
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Map Overview */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <MapIcon color="primary" />
            <Typography variant="h6" fontWeight="600">
              Service Coverage Map
            </Typography>
          </Box>
          <Box 
            sx={{ 
              height: 200, 
              backgroundColor: alpha(theme.palette.primary.main, 0.1),
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: `2px dashed ${theme.palette.primary.main}`
            }}
          >
            <Typography color="text.secondary">
              Interactive Map Coming Soon
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* Controls */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              placeholder="Search areas..."
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ minWidth: 300 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
            
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value="all"
                label="Status"
                onChange={() => {}} // Add filter logic
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Coverage</InputLabel>
              <Select
                value="all"
                label="Coverage"
                onChange={() => {}} // Add filter logic
              >
                <MenuItem value="all">All Coverage</MenuItem>
                <MenuItem value="full">Full</MenuItem>
                <MenuItem value="partial">Partial</MenuItem>
                <MenuItem value="planning">Planning</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </CardContent>
      </Card>

      {/* Service Areas Table */}
      <Card>
        <TableContainer>
          <Table>
            <TableHead sx={{ bgcolor: alpha(theme.palette.primary.main, 0.04) }}>
              <TableRow>
                <TableCell>Area</TableCell>
                <TableCell>Zip Codes</TableCell>
                <TableCell>Cleaners</TableCell>
                <TableCell>Customers</TableCell>
                <TableCell>Avg. Price</TableCell>
                <TableCell>Coverage</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredAreas.map((area) => (
                <TableRow key={area.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
                        <LocationIcon />
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle1" fontWeight="600">
                          {area.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {area.city}, {area.state}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxWidth: 200 }}>
                      {area.zipCodes.slice(0, 3).map(zip => (
                        <Chip key={zip} label={zip} size="small" variant="outlined" />
                      ))}
                      {area.zipCodes.length > 3 && (
                        <Chip 
                          label={`+${area.zipCodes.length - 3}`} 
                          size="small" 
                          color="primary"
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <PeopleIcon color="primary" fontSize="small" />
                      <Typography fontWeight="600">
                        {area.cleaners}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography fontWeight="600">
                      {area.customers}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {area.avgPrice > 0 ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <MoneyIcon color="primary" fontSize="small" />
                        <Typography fontWeight="600" color="primary">
                          ${area.avgPrice}
                        </Typography>
                      </Box>
                    ) : (
                      <Typography color="text.secondary">-</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={area.coverage} 
                      color={getCoverageColor(area.coverage)}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Switch 
                      checked={area.status} 
                      color="success"
                      onChange={() => {}} // Add toggle logic
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton 
                      size="small" 
                      onClick={() => handleOpenDialog(area)}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton size="small">
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Add/Edit Area Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingArea ? 'Edit Service Area' : 'Add New Service Area'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Area Name"
                  value={newArea.name}
                  onChange={(e) => setNewArea({ ...newArea, name: e.target.value })}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="City"
                  value={newArea.city}
                  onChange={(e) => setNewArea({ ...newArea, city: e.target.value })}
                  fullWidth
                />
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>State</InputLabel>
                  <Select
                    value={newArea.state}
                    label="State"
                    onChange={(e) => setNewArea({ ...newArea, state: e.target.value })}
                  >
                    {states.map(state => (
                      <MenuItem key={state} value={state}>{state}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Coverage</InputLabel>
                  <Select
                    value={newArea.coverage}
                    label="Coverage"
                    onChange={(e) => setNewArea({ ...newArea, coverage: e.target.value })}
                  >
                    <MenuItem value="Full">Full Coverage</MenuItem>
                    <MenuItem value="Partial">Partial Coverage</MenuItem>
                    <MenuItem value="Planning">Planning Phase</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <TextField
              label="Zip Codes"
              value={newArea.zipCodes.join(', ')}
              onChange={handleZipCodesChange}
              placeholder="e.g., 10001, 10002, 10003"
              helperText="Enter zip codes separated by commas"
              fullWidth
            />

            <FormControlLabel
              control={
                <Switch
                  checked={newArea.status}
                  onChange={(e) => setNewArea({ ...newArea, status: e.target.checked })}
                  color="success"
                />
              }
              label="Active Area"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSaveArea} variant="contained">
            {editingArea ? 'Update Area' : 'Create Area'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}